import {
  Component,
  Inject,
  PLATFORM_ID,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subscription, BehaviorSubject } from 'rxjs';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { TokenService } from './shared/services/token.service';
import { RootService } from './shared/services/root.service';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { LoaderService } from './shared/services/loader.service';
import { AuthService } from './shared/services/auth.service';
import { LogoutDetectionService } from './shared/services/logout-detection.service';
import { InactivityService } from './shared/services/inactivity.service';
import { NotificationComponent } from './shared/components/notification/notification.component';
import { ModalService } from './shared/services/modal.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    RouterModule,
    FooterComponent,
    LoaderComponent,
    NotificationComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Rewire';
  currentRoute$ = new BehaviorSubject<string>('');
  currentRoute: string = '';
  private sub: Subscription = new Subscription();

  constructor(
    private router: Router,
    private tokenService: TokenService,
    private rootService: RootService,
    private authService: AuthService,
    private logoutDetectionService: LogoutDetectionService,
    private inactivityService: InactivityService,
    @Inject(PLATFORM_ID) private platformId: Object,
    public loaderService: LoaderService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      const userCode = localStorage.getItem('userCode');
      if (userCode) {
        // this.router.navigate(['/home']);
      }

      // Initialize route immediately in constructor for SSR compatibility
      this.currentRoute = this.router.url;
      this.currentRoute$.next(this.router.url);
    }
  }

  ngOnInit(): void {
    // Initialize authentication state
    this.authService.initializeAuthState();

    // Initialize token refresh scheduling
    this.initializeTokenRefresh();

    // Initialize logout detection
    this.logoutDetectionService.initializeLogoutDetection();

    // Initialize inactivity detection
    this.inactivityService.initialize();

    // Subscribe to auth state changes to restart inactivity detection on login
    this.sub.add(
      this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          // User logged in - restart inactivity detection
          this.inactivityService.restart();
        } else {
          // User logged out - stop inactivity detection
          this.inactivityService.stop();
        }
      }),
    );

    // Set initial route
    this.currentRoute = this.router.url;
    this.currentRoute$.next(this.router.url);

    // Subscribe to router events with proper error handling
    this.sub.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe({
          next: (event: any) => {
            // Use setTimeout(0) to defer route update to avoid ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
              this.currentRoute = event.url;
              this.currentRoute$.next(event.url);

              // Hide loader on route change to prevent stuck loader
              // This is especially important after payment success navigation
              this.loaderService.hide();

              // Manually trigger change detection after all updates
              this.modalService.closeAllModals();
              this.cdr.detectChanges();
            }, 0);
          },
          error: (error) => {
            console.error('Router event subscription error:', error);
            // Hide loader on error as well
            this.loaderService.hide();
          },
        }),
    );
  }

  private initializeTokenRefresh(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const accessToken = this.tokenService.getAccessToken();
    const userCode = this.tokenService.getUserCode();

    if (accessToken) {
      // Schedule token refresh based on user authentication status
      if (userCode) {
        // Logged in user - schedule refresh with 10-second buffer
        this.tokenService.scheduleTokenRefresh(() => {
          this.rootService.refreshToken2().subscribe({
            next: (response) => {
              if (response?.success) {
                // Re-schedule next refresh
                this.initializeTokenRefresh();
              } else {
                this.fallbackToTokenGeneration();
              }
            },
            error: (error) => {
              // Fall back to token generation
              this.fallbackToTokenGeneration();
            },
          });
        }, 10);
      } else {
        // Free search user - schedule token generation with 10-second buffer
        this.tokenService.scheduleTokenRefresh(() => {
          this.rootService
            .generateTokenForFreeSearch()
            .then(() => {
              // Re-schedule next refresh
              this.initializeTokenRefresh();
            })
            .catch((error) => {
              console.error('❌ Free search token generation failed:', error);
            });
        }, 10);
      }
    } else {
      // No token - generate one for free search
      this.rootService
        .generateTokenForFreeSearch()
        .then(() => {
          // Schedule next refresh
          this.initializeTokenRefresh();
        })
        .catch((error) => {
          console.error('❌ Initial token generation failed:', error);
        });
    }
  }

  private fallbackToTokenGeneration(): void {
    this.rootService
      .generateTokenForFreeSearch()
      .then(() => {
        // Re-schedule next refresh
        this.initializeTokenRefresh();
      })
      .catch((error) => {
        console.error('❌ Fallback token generation failed:', error);
      });
  }

  ngOnDestroy(): void {
    // Clear any scheduled token refresh
    this.tokenService.clearRefreshTimer();

    if (this.sub) {
      this.sub.unsubscribe();
    }
    // Clean up logout detection service
    this.logoutDetectionService.destroy();
    // Clean up inactivity service
    this.inactivityService.destroy();
  }
}
